/**
 * PauseAndGoOverlay - PauseAndGo 트리거 시 비디오 위에 표시되는 오버레이 컴포넌트
 *
 * 타입별로 다른 UI를 렌더링:
 * - PAUSE_AND_GO: 메시지 + 확인 버튼
 * - PAUSE_AND_AUTOGO: 메시지 + 카운트다운 타이머 + 조기 확인 버튼
 * - PAUSE_AND_REPEAT: 메시지 + 예/아니오 버튼
 * - PAUSE_AND_QUIZ: 퀴즈 API 연결 시 실제 퀴즈 렌더링, 미연결 시 수동 결과 선택
 * - PAUSE_AND_VIDEO: 관련 깨처 영상 안내 + 확인/건너뛰기 버튼
 */
import { useState, useEffect, useCallback } from 'react'
import { Loader2 } from 'lucide-react'
import type { QuizScript, QuizSolveRequest, SolveResult } from '../../types/quizScript'
import type { QuizSolveResult, QuizData, MultiChoiceQuizData } from '../../types/legacyQuiz'
import { QuizRenderer } from '../quiz-types'
import type { PauseAndGoItem, PauseAndGoProgressEntry, QuetureTestLabels, QuetureTestQuizApi } from './types'
import { PAUSE_AND_GO_TYPE_LABELS, PAUSE_AND_GO_TYPE_COLORS } from './constants'

interface PauseAndGoOverlayProps {
  pag: PauseAndGoItem
  onComplete: (pagId: number, result: PauseAndGoProgressEntry['result']) => void
  onDismiss: () => void
  labels: QuetureTestLabels
  quizApi?: QuetureTestQuizApi
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

interface PrimaryButtonProps {
  onClick: () => void
  children: React.ReactNode
  className?: string
}

function PrimaryButton({ onClick, children, className = '' }: PrimaryButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-5 py-2 font-medium text-white bg-indigo-600 rounded-lg transition-colors hover:bg-indigo-700 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${className}`}
    >
      {children}
    </button>
  )
}

interface SecondaryButtonProps {
  onClick: () => void
  children: React.ReactNode
  className?: string
}

function SecondaryButton({ onClick, children, className = '' }: SecondaryButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-5 py-2 font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${className}`}
    >
      {children}
    </button>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Type-specific content components
// ─────────────────────────────────────────────────────────────────────────────

function PauseAndGoContent({
  pag,
  onComplete,
  labels,
}: Pick<PauseAndGoOverlayProps, 'pag' | 'onComplete' | 'labels'>) {
  return (
    <div className="space-y-6">
      <p className="text-base text-gray-800">
        {pag.msg ?? labels.pauseAndGoDefaultMsg}
      </p>
      <div className="flex justify-center">
        <PrimaryButton onClick={() => onComplete(pag.id, 'confirmed')}>
          {pag.okButtonMessage ?? labels.confirm}
        </PrimaryButton>
      </div>
    </div>
  )
}

function PauseAndAutoGoContent({
  pag,
  onComplete,
  labels,
}: Pick<PauseAndGoOverlayProps, 'pag' | 'onComplete' | 'labels'>) {
  const initialSeconds = Math.ceil((pag.pauseDuration ?? 5000) / 1000)
  const [remaining, setRemaining] = useState(initialSeconds)

  useEffect(() => {
    if (remaining <= 0) {
      onComplete(pag.id, 'auto')
      return
    }
    const timer = setInterval(() => {
      setRemaining((prev) => prev - 1)
    }, 1000)
    return () => clearInterval(timer)
  }, [remaining, pag.id, onComplete])

  return (
    <div className="space-y-6">
      {pag.msg && <p className="text-base text-gray-800">{pag.msg}</p>}
      {/* Countdown */}
      <div className="flex flex-col gap-1 items-center">
        <span className="text-5xl font-bold text-indigo-600 tabular-nums">{remaining}</span>
        <span className="text-sm text-gray-500">{labels.autoResumeIn}</span>
      </div>
      <div className="flex justify-center">
        <SecondaryButton onClick={() => onComplete(pag.id, 'confirmed')}>
          {labels.confirm}
        </SecondaryButton>
      </div>
    </div>
  )
}

function PauseAndRepeatContent({
  pag,
  onComplete,
  labels,
}: Pick<PauseAndGoOverlayProps, 'pag' | 'onComplete' | 'labels'>) {
  return (
    <div className="space-y-6">
      <p className="text-base text-gray-800">
        {pag.msg ?? labels.repeatDefaultMsg}
      </p>
      <div className="flex gap-3 justify-center">
        <PrimaryButton onClick={() => onComplete(pag.id, 'yes')}>
          {pag.okButtonMessage ?? labels.yes}
        </PrimaryButton>
        <SecondaryButton onClick={() => onComplete(pag.id, 'no')}>
          {pag.notOkButtonMessage ?? labels.no}
        </SecondaryButton>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// PauseAndQuiz - 실제 퀴즈 로드 및 풀기
// ─────────────────────────────────────────────────────────────────────────────

function PauseAndQuizContent({
  pag,
  onComplete,
  labels,
  quizApi,
}: Pick<PauseAndGoOverlayProps, 'pag' | 'onComplete' | 'labels' | 'quizApi'>) {
  const hasApi = !!(quizApi?.getQuiz && quizApi?.solve)

  const [quizData, setQuizData] = useState<QuizScript | null>(null)
  const [legacyQuiz, setLegacyQuiz] = useState<QuizData | null>(null)
  const [solveResult, setSolveResult] = useState<QuizSolveResult | null>(null)
  const [submittedAnswers, setSubmittedAnswers] = useState<string[]>([])
  const [isLoadingQuiz, setIsLoadingQuiz] = useState(false)
  const [isSolving, setIsSolving] = useState(false)
  const [quizError, setQuizError] = useState<string | null>(null)

  // QuizScript → legacyQuiz 변환
  const toLegacyQuiz = useCallback((data: QuizScript): QuizData => {
    const rawData = data as unknown as Record<string, unknown>
    const qd = (rawData.quizData as QuizData) || data
    return {
      ...qd,
      type: data.type || qd.type,
      title: data.title || qd.title,
      problem: data.problem || qd.problem,
      vars: data.vars || qd.vars,
      image: data.image || qd.image,
      imageMobile: data.imageMobile || qd.imageMobile,
    } as QuizData
  }, [])

  // 퀴즈 로드
  useEffect(() => {
    if (!hasApi || !pag.quizId) return

    let cancelled = false
    setIsLoadingQuiz(true)
    setQuizError(null)

    quizApi!.getQuiz(pag.quizId, false)
      .then((data) => {
        if (cancelled) return
        setQuizData(data)
        setLegacyQuiz(toLegacyQuiz(data))
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to fetch quiz for PAG:', err)
        setQuizError(labels.quizLoadFailed)
      })
      .finally(() => {
        if (!cancelled) setIsLoadingQuiz(false)
      })

    return () => { cancelled = true }
  }, [hasApi, pag.quizId, quizApi, toLegacyQuiz, labels.quizLoadFailed])

  // 퀴즈 풀기
  const handleSolve = useCallback(
    async (
      _quizId: string | number,
      answers: string[],
      quizVars: Record<string, unknown>,
      elapsedTime: number
    ) => {
      if (!quizData || !quizApi || isSolving) return

      setSubmittedAnswers(answers)
      setIsSolving(true)

      const legacy = legacyQuiz
      if (!legacy) {
        setIsSolving(false)
        return
      }

      try {
        // Transform answers based on quiz type (same as useQuizTest)
        let transformedAnswers: (string | number | boolean)[] = []

        switch (legacy.type) {
          case 'fill_blank':
            transformedAnswers = answers
            break
          case 'multi_choice': {
            const quiz = legacy as MultiChoiceQuizData
            transformedAnswers = answers
              .map((key) => {
                const index = quiz.choices.findIndex((choice) => choice.key === key)
                return index >= 0 ? index + 1 : 0
              })
              .filter((idx) => idx > 0)
            break
          }
          case 'select_text':
            transformedAnswers = answers.map((a) => {
              const num = parseInt(a, 10)
              return isNaN(num) ? 0 : num
            })
            break
          case 'boolean':
            transformedAnswers = answers.map((a) => a === 'true')
            break
          default:
            transformedAnswers = answers
        }

        const request: QuizSolveRequest = {
          answers: transformedAnswers,
          vars: quizVars,
          elapsed: elapsedTime,
        }

        const quizIdForSolve = quizData.quizId
        if (!quizIdForSolve) throw new Error('Quiz ID is missing')

        const apiResult: SolveResult = await quizApi.solve(String(quizIdForSolve), request)

        const result: QuizSolveResult = {
          correct: apiResult.correct,
          corrects: apiResult.corrects.map((c) => {
            if (typeof c === 'boolean') return c
            if (c === 'true' || c === true) return true
            if (c === 'false' || c === false) return false
            return Boolean(c)
          }),
          correctAnswers: (apiResult.correctAnswers ?? []).map((a) => String(a)),
          elapsedTime,
          description: apiResult.description
            ? {
                text: apiResult.description.text ?? undefined,
                image: (() => {
                  const desc = apiResult.description as Record<string, unknown>
                  const img = desc.image
                  if (typeof img === 'string') return { signedUrl: img }
                  if (img && typeof img === 'object') return img as { signedUrl: string }
                  return undefined
                })(),
                imageMobile: (() => {
                  const desc = apiResult.description as Record<string, unknown>
                  const img = desc.imageMobile
                  if (typeof img === 'string') return { signedUrl: img }
                  if (img && typeof img === 'object') return img as { signedUrl: string }
                  return undefined
                })(),
                desmos: apiResult.description.desmos ?? undefined,
              }
            : undefined,
        }

        setSolveResult(result)
      } catch (err) {
        console.error('Failed to solve quiz:', err)
        // 실패 시 수동 선택으로 전환
        setSolveResult({
          correct: false,
          corrects: [],
          correctAnswers: [],
          elapsedTime,
        })
      } finally {
        setIsSolving(false)
      }
    },
    [quizData, legacyQuiz, quizApi, isSolving]
  )

  // API 없거나 quizId 없는 경우: 수동 결과 선택
  if (!hasApi || !pag.quizId) {
    return (
      <div className="space-y-6">
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-500">{labels.quizApiNotConnected}</p>
          {pag.quizId && (
            <p className="mt-1 font-mono text-xs text-gray-400">
              {labels.quizIdRef}: {pag.quizId}
            </p>
          )}
        </div>
        <p className="text-sm font-medium text-gray-700">{labels.manualResult}</p>
        <div className="flex gap-3 justify-center">
          <PrimaryButton
            className="bg-green-600 hover:bg-green-700 focus:ring-green-500"
            onClick={() => onComplete(pag.id, 'correct')}
          >
            {labels.correct}
          </PrimaryButton>
          <SecondaryButton onClick={() => onComplete(pag.id, 'incorrect')}>
            {labels.incorrect}
          </SecondaryButton>
        </div>
      </div>
    )
  }

  // 퀴즈 로딩 중
  if (isLoadingQuiz) {
    return (
      <div className="flex flex-col items-center gap-4 py-8">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
        <p className="text-sm text-gray-600">{labels.quizLoading}</p>
      </div>
    )
  }

  // 퀴즈 로드 실패 → 수동 선택 fallback
  if (quizError || !quizData || !legacyQuiz) {
    return (
      <div className="space-y-6">
        <div className="p-4 bg-red-50 rounded-lg border border-red-200">
          <p className="text-sm text-red-700">{quizError || labels.quizLoadFailed}</p>
          <p className="mt-1 font-mono text-xs text-red-500">
            Quiz ID: {pag.quizId}
          </p>
        </div>
        <p className="text-sm font-medium text-gray-700">{labels.manualResult}</p>
        <div className="flex gap-3 justify-center">
          <PrimaryButton
            className="bg-green-600 hover:bg-green-700 focus:ring-green-500"
            onClick={() => onComplete(pag.id, 'correct')}
          >
            {labels.correct}
          </PrimaryButton>
          <SecondaryButton onClick={() => onComplete(pag.id, 'incorrect')}>
            {labels.incorrect}
          </SecondaryButton>
        </div>
      </div>
    )
  }

  // 퀴즈 렌더링
  return (
    <div className="space-y-4">
      <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
        <p className="text-xs text-purple-600">
          <span className="font-medium">Quiz ID:</span>{' '}
          <span className="font-mono">{pag.quizId}</span>
          <span className="mx-2">·</span>
          <span className="font-medium">Type:</span>{' '}
          <span className="font-mono">{legacyQuiz.type}</span>
        </p>
      </div>

      <div className="min-h-[200px]">
        <QuizRenderer
          key={`pag-quiz-${pag.quizId}-${solveResult ? 'solved' : 'unsolved'}`}
          quizType={legacyQuiz.type}
          quizId={quizData.quizId ?? ''}
          quiz={legacyQuiz}
          onSolve={handleSolve}
          solveResult={solveResult}
          showReallyTrue={false}
          useKeypad={false}
          initialAnswers={submittedAnswers.length > 0 ? submittedAnswers : undefined}
        />
      </div>

      {/* 채점 중 표시 */}
      {isSolving && (
        <div className="flex gap-2 justify-center items-center text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">{labels.solving}</span>
        </div>
      )}

      {/* 채점 완료 후 사용자가 결과 확인하고 닫기 */}
      {solveResult && (
        <div className="flex justify-center pt-3 border-t border-gray-100">
          <PrimaryButton
            className={solveResult.correct ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            onClick={() => onComplete(pag.id, solveResult.correct ? 'correct' : 'incorrect')}
          >
            {solveResult.correct ? labels.correct : labels.incorrect} — {labels.confirm}
          </PrimaryButton>
        </div>
      )}
    </div>
  )
}

function PauseAndVideoContent({
  pag,
  onComplete,
  labels,
}: Pick<PauseAndGoOverlayProps, 'pag' | 'onComplete' | 'labels'>) {
  return (
    <div className="space-y-6">
      <p className="text-base text-gray-800">{labels.videoDefaultMsg}</p>
      {pag.videoKey && (
        <div className="p-3 bg-pink-50 rounded-lg border border-pink-200">
          <p className="font-mono text-xs text-pink-600">
            Video Key: {pag.videoKey}
          </p>
        </div>
      )}
      <div className="flex gap-3 justify-center">
        <PrimaryButton onClick={() => onComplete(pag.id, 'confirmed')}>
          {labels.watchRelatedQueture}
        </PrimaryButton>
        <SecondaryButton onClick={() => onComplete(pag.id, 'confirmed')}>
          {labels.skip}
        </SecondaryButton>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Main overlay component
// ─────────────────────────────────────────────────────────────────────────────

export function PauseAndGoOverlay({
  pag,
  onComplete,
  onDismiss,
  labels,
  quizApi,
}: PauseAndGoOverlayProps) {
  const typeLabel = PAUSE_AND_GO_TYPE_LABELS[pag.type]
  const typeColorClass = PAUSE_AND_GO_TYPE_COLORS[pag.type]

  // PAUSE_AND_QUIZ일 때 오버레이를 더 크게
  const isQuizType = pag.type === 'PAUSE_AND_QUIZ'

  function renderContent() {
    switch (pag.type) {
      case 'PAUSE_AND_GO':
        return <PauseAndGoContent pag={pag} onComplete={onComplete} labels={labels} />
      case 'PAUSE_AND_AUTOGO':
        return <PauseAndAutoGoContent pag={pag} onComplete={onComplete} labels={labels} />
      case 'PAUSE_AND_REPEAT':
        return <PauseAndRepeatContent pag={pag} onComplete={onComplete} labels={labels} />
      case 'PAUSE_AND_QUIZ':
        return (
          <PauseAndQuizContent
            pag={pag}
            onComplete={onComplete}
            labels={labels}
            quizApi={quizApi}
          />
        )
      case 'PAUSE_AND_VIDEO':
        return <PauseAndVideoContent pag={pag} onComplete={onComplete} labels={labels} />
      default:
        return null
    }
  }

  return (
    /* Backdrop */
    <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/60 overflow-y-auto">
      {/* Content card */}
      <div className={`relative w-full mx-4 bg-white rounded-xl shadow-2xl p-6 ${
        isQuizType ? 'max-w-2xl max-h-[90%] overflow-y-auto' : 'max-w-md'
      }`}>
        {/* Type badge */}
        <span
          className={`absolute top-4 right-4 px-2.5 py-1 rounded-full text-xs font-semibold ${typeColorClass}`}
        >
          {typeLabel}
        </span>

        {/* PAG ID (debug reference) */}
        <p className="mb-4 text-xs font-mono text-gray-400">PAG #{pag.id}</p>

        {/* Type-specific content */}
        {renderContent()}

        {/* Dismiss link */}
        <div className="mt-6 flex justify-center border-t border-gray-100 pt-4">
          <button
            type="button"
            onClick={onDismiss}
            className="text-xs text-gray-400 underline underline-offset-2 hover:text-gray-600 transition-colors"
          >
            {labels.dismissNoComplete}
          </button>
        </div>
      </div>
    </div>
  )
}

export default PauseAndGoOverlay
